import { adminFetch } from "./base";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type NotificationSeverity = "info" | "warning" | "error" | "success";

export interface AdminNotification {
  readonly id: string;
  readonly title: string;
  readonly message: string;
  readonly severity: NotificationSeverity;
  readonly link?: string;
  readonly is_read: boolean;
  readonly read_at?: string;
  readonly created_at: string;
}

export interface NotificationListResponse {
  readonly notifications: readonly AdminNotification[];
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
}

export interface UnreadCountResponse {
  readonly count: number;
}

export interface NotificationFilters {
  readonly severity?: NotificationSeverity;
  readonly is_read?: boolean;
  readonly date_from?: string;
  readonly date_to?: string;
  readonly page?: number;
  readonly limit?: number;
}

// ---------------------------------------------------------------------------
// API module
// ---------------------------------------------------------------------------

function buildQuery(filters: NotificationFilters): string {
  const params = new URLSearchParams();
  if (filters.severity) params.set("severity", filters.severity);
  if (filters.is_read !== undefined) params.set("is_read", String(filters.is_read));
  if (filters.date_from) params.set("date_from", filters.date_from);
  if (filters.date_to) params.set("date_to", filters.date_to);
  if (filters.page) params.set("page", String(filters.page));
  if (filters.limit) params.set("limit", String(filters.limit));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/** Admin notification endpoints. */
export const notificationsApi = {
  /** Fetch paginated list of notifications for the authenticated admin. */
  list: (filters: NotificationFilters = {}) =>
    adminFetch<NotificationListResponse>(
      `/api/admin/notifications${buildQuery(filters)}`,
    ),

  /** Fetch unread notification count. */
  unreadCount: () =>
    adminFetch<UnreadCountResponse>("/api/admin/notifications/unread-count"),

  /** Mark a single notification as read. */
  markRead: (id: string) =>
    adminFetch<{ success: boolean }>(`/api/admin/notifications/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ is_read: true }),
    }),

  /** Mark all notifications as read. */
  markAllRead: () =>
    adminFetch<{ success: boolean }>("/api/admin/notifications/read-all", {
      method: "POST",
    }),

  /** Delete notifications by ids. */
  deleteMany: (ids: readonly string[]) =>
    adminFetch<{ success: boolean }>("/api/admin/notifications", {
      method: "DELETE",
      body: JSON.stringify({ ids }),
    }),
} as const;
